import React, { FC, useEffect, useMemo, useRef } from 'react';
import { IntlProvider } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import browserLanguage from 'in-browser-language';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/nl';
import 'moment/locale/pl';
import 'moment/locale/pt';
import 'moment/locale/ru';
import 'moment/locale/uk';

import { IState } from './reducers';
import { IContextReducer } from './reducers/contextReducer';
import { ILanguageReducer } from './reducers/languageReducer';
import { getLanguages, setUserLanguage, getTranslationsForCurrentLang, savePrefferedLanguage } from './actions/languageActions';
import { IConfigReducer } from './reducers/configReducer';
import { ELcid } from './entities/ILanguage';
import { languageList } from './tools/languageTools';
import { isPageWithoutAutorization } from './constants/routes';
import { IProfileReducer } from './reducers/profileReducer';
import { getCurrentUserProfileNew } from './actions/profileActions';
import { selectAppDefaultLanguage } from './tools/platformConfiguration';

export interface IReconciledIntlProps {
    intl: { formatMessage?(config: { id: string }): string };
}

type GetCurrentUserProfileNew = ReturnType<typeof getCurrentUserProfileNew>;
type GetLanguages = ReturnType<typeof getLanguages>;
type SetUserLanguage = ReturnType<typeof setUserLanguage>;
type GetTranslationsForCurrentLang = ReturnType<typeof getTranslationsForCurrentLang>;
type savePrefferedLanguage = ReturnType<typeof savePrefferedLanguage>;

export const IntlProviderComponent: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const dispatch = useDispatch();
    const queryString = globalThis.location.search;
    const params = new URLSearchParams(queryString);
    const didMountRef = useRef(false);
    const { isLoggedIn, userLanguage, appConfig, translations, currentUserProfile, appDefaultLanguage } = useSelector<IState, IContextReducer & ILanguageReducer & IConfigReducer & IProfileReducer & { appDefaultLanguage: string }>(state => ({
        ...state.context,
        ...state.language,
        ...state.config,
        ...state.profile,
        appDefaultLanguage: selectAppDefaultLanguage(state)
    }));

    const resolvedDefaultLanguage = appDefaultLanguage || appConfig.defaultLanguage;

    const browserUserLanguage = useMemo(() => {
        return browserLanguage.pick(['fr', 'en', 'pl', 'es', 'it', 'uk', 'ru', 'de', 'pt'], resolvedDefaultLanguage);
    }, [resolvedDefaultLanguage]);

    const setLanguage = (lcid: number) => {
        moment.locale(languageList[lcid]);
        dispatch<SetUserLanguage>(setUserLanguage(Number(lcid)));
        dispatch<GetTranslationsForCurrentLang>(getTranslationsForCurrentLang(Number(lcid)));
    };

    const getInitialLanguageLcid = (managedLanguages = []) => {
        const normalizedBrowserLanguage = (browserUserLanguage || '').toLowerCase();
        const normalizedDefaultLanguage = (resolvedDefaultLanguage || '').toLowerCase();

        const findByCode = (code: string) => managedLanguages.find(language => language.Code?.toLowerCase() === code);

        return (
            findByCode(normalizedBrowserLanguage)?.Lcid
            || findByCode(normalizedDefaultLanguage)?.Lcid
            || managedLanguages.find(language => language.DefaultLanguage)?.Lcid
            || findByCode('en')?.Lcid
            || managedLanguages[0]?.Lcid
            || ELcid.En
        );
    };

    const initLanguages = () => {
        dispatch<GetLanguages>(getLanguages()).then(managedLanguages => {
            const selectedLcid = getInitialLanguageLcid(managedLanguages);
            dispatch<savePrefferedLanguage>(savePrefferedLanguage(Number(selectedLcid)));
            setLanguage(selectedLcid);
        });
    };

    useEffect(() => {
        if (params.get('lcid')) {
            setLanguage(Number(params.get('lcid')));
        } else if (didMountRef.current) {
            if (isLoggedIn && !currentUserProfile) {
                dispatch<GetCurrentUserProfileNew>(getCurrentUserProfileNew()).then(res => {
                    if (res.preferredLanguage > 0) {
                        setLanguage(res.preferredLanguage);
                    } else {
                        initLanguages();
                    }
                })
                .catch((err) => {
                    initLanguages();
                });
            } else if (!currentUserProfile) {
                initLanguages();
            }
        }
        if (isPageWithoutAutorization()) {
            initLanguages();
        }
        didMountRef.current = true;
    }, [isLoggedIn, currentUserProfile]);

    const messages = useMemo(() => translations[userLanguage], [translations, userLanguage]);
    const locale = useMemo(() => languageList[userLanguage || ELcid.En], [userLanguage]);

    const safeChunks = (chunks: unknown) => {
        const normalizedChunks = Array.isArray(chunks) ? chunks : [chunks];
        return normalizedChunks.map(c => {
            if (typeof c === 'string') return c;
            if (c === null || c === undefined) return '';
            if (typeof c === 'number' || typeof c === 'boolean') return String(c);
            return '';
        }).join('');
    };

    return (
        <IntlProvider locale={locale} messages={messages || {}} defaultRichTextElements={{
            br: () => '<br />',
            span: (chunks) => `<span>${safeChunks(chunks)}</span>`,
            em: (chunks) => `<em>${safeChunks(chunks)}</em>`,
            li: (chunks) => `<li>${safeChunks(chunks)}</li>`,
            b: (chunks) => `<b>${safeChunks(chunks)}</b>`,
            i: (chunks) => `<i>${safeChunks(chunks)}</i>`,
            ul: (chunks) => `<ul>${safeChunks(chunks)}</ul>`,
            strong: (chunks) => `<strong>${safeChunks(chunks)}</strong>`
        }}>
            {children}
        </IntlProvider>
    );
};

export default IntlProviderComponent;